/* markdown-lite — the custom renderer's first pass. Handles what the
   chat actually uses: fenced code blocks, `inline code`, **bold**,
   __underline__, ~~strike~~, *italic* / _italic_, and @mentions.
   Deliberately non-nested — tokens don't recurse. A real parser lands
   later; this keeps messages honest today. */

"use client";

import { useState, type ReactNode } from "react";
import { Emoji, EMOJI_INDEX, EMOJI_ALT } from "./emoji";

/* ||spoiler|| — black-boxes the text until tapped; stays revealed.
   spoilered text skips further formatting (it's one opaque token) */
function Spoiler({ text }: { text: string }) {
  const [shown, setShown] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setShown(true)}
      className={`inline rounded px-1 align-baseline text-[0.95em] transition ${
        shown
          ? "bg-ink/60 text-fg-mute ring-1 ring-line"
          : "cursor-pointer bg-ink text-transparent select-none"
      }`}
      title={shown ? undefined : "spoiler — tap to reveal"}
    >
      {text}
    </button>
  );
}

/* markdown tokens + every sheeted emoji char — emoji land in the same
   match stream so they render as sprite cells inside any line */
const INLINE = new RegExp(
  "(!\\[[^\\]]*\\]\\(https?://[^)\\n]+\\)|`[^`\\n]+`|\\|\\|[^\\n]+\\|\\||\\*\\*[^*\\n]+\\*\\*|__[^_\\n]+__|~~[^~\\n]+~~|\\*[^*\\n]+\\*|_[^_\\n]+_|@[a-zA-Z0-9]+|" +
    EMOJI_ALT +
    ")",
  "g",
);

/* @name resolution — the caller maps a typed handle to a member (or
   the local account). hit.click opens the profile; hit.me paints the
   "you were pinged" highlight */
export type MentionLookup = (
  name: string,
) => { click?: () => void; me?: boolean } | undefined;

const inline = (line: string, k: string, mention?: MentionLookup): ReactNode[] => {
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  INLINE.lastIndex = 0;
  while ((m = INLINE.exec(line))) {
    if (m.index > last) out.push(line.slice(last, m.index));
    const t = m[0];
    const key = `${k}-${m.index}`;
    if (EMOJI_INDEX.has(t)) {
      out.push(<Emoji key={key} char={t} size={17} />);
      last = m.index + t.length;
      continue;
    }
    /* ![alt](url) — inline media embed (the gif picker writes these) */
    if (t.startsWith("![")) {
      const src = t.slice(t.indexOf("](") + 2, -1);
      out.push(
        // eslint-disable-next-line @next/next/no-img-element -- remote gif embed
        <img
          key={key}
          src={src}
          alt={t.slice(2, t.indexOf("]"))}
          loading="lazy"
          className="my-1 max-h-52 rounded-lg ring-1 ring-line"
        />,
      );
      last = m.index + t.length;
      continue;
    }
    /* ||spoiler|| — blurred until tapped, stays revealed after */
    if (t.startsWith("||")) {
      out.push(<Spoiler key={key} text={t.slice(2, -2)} />);
      last = m.index + t.length;
      continue;
    }
    if (t.startsWith("`"))
      out.push(
        <code
          key={key}
          className="rounded bg-ink/80 px-1 py-0.5 font-mono text-[0.92em] text-fg ring-1 ring-line"
        >
          {t.slice(1, -1)}
        </code>,
      );
    else if (t.startsWith("**")) out.push(<strong key={key}>{t.slice(2, -2)}</strong>);
    else if (t.startsWith("__")) out.push(<u key={key}>{t.slice(2, -2)}</u>);
    else if (t.startsWith("~~")) out.push(<s key={key}>{t.slice(2, -2)}</s>);
    else if (t.startsWith("*") || t.startsWith("_"))
      out.push(<em key={key}>{t.slice(1, -1)}</em>);
    else {
      /* @mention — the resolver decides if it's a real member (click
         opens the profile) and whether it pings the local account
         (amber, discord's "you got pinged" tint) */
      const hit = mention?.(t.slice(1));
      if (hit)
        out.push(
          <button
            key={key}
            type="button"
            onClick={hit.click}
            className={`rounded-sm px-0.5 font-semibold transition ${
              hit.me
                ? "bg-idle/25 text-idle hover:bg-idle/40"
                : "bg-accent/15 text-accent-2 hover:bg-accent/30"
            }`}
          >
            {t}
          </button>,
        );
      else
        out.push(
          <span
            key={key}
            className="rounded-sm bg-accent/15 px-0.5 font-semibold text-accent-2"
          >
            {t}
          </span>,
        );
    }
    last = m.index + t.length;
  }
  if (last < line.length) out.push(line.slice(last));
  return out;
};

/* text → nodes: ``` fences become <pre> blocks; everything else is
   inline-parsed per line with <br> joins */
export function mdRender(text: string, mention?: MentionLookup): ReactNode {
  const parts = text.split(/```\n?/);
  const out: ReactNode[] = [];
  parts.forEach((part, pi) => {
    if (pi % 2 === 1) {
      /* fenced block — raw, no inline parsing */
      out.push(
        <pre
          key={`pre${pi}`}
          className="mt-1 mb-0.5 overflow-x-auto rounded-lg bg-ink/80 p-2.5 font-mono text-micro leading-relaxed text-fg ring-1 ring-line scroll-dark"
        >
          {part.replace(/\n$/, "")}
        </pre>,
      );
      return;
    }
    const lines = part.split("\n");
    lines.forEach((line, li) => {
      if (pi > 0 || li > 0) out.push(<br key={`br${pi}-${li}`} />);
      out.push(...inline(line, `${pi}-${li}`, mention));
    });
  });
  return out;
}
